import { ProductColor } from '@components/Product/Details/_ProductSelection';
import { LinkProps } from '@repo/ui/src/types/link';
import useTranslation from '@components/Translation/useTranslation';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { classNames } from '@repo/ui/src/utils/classNames';
import { MediaType } from '@repo/ui/src/types/image';

export const COLOR_STYLES = {
  bronze: 'tw-bg-bronzecircle',
  blue: 'tw-bg-[#1A2252]',
  red: 'tw-bg-alert-400',
  black: 'tw-bg-primary-300',
  '': '',
};

interface ColorItemProps {
  color: keyof typeof COLOR_STYLES;
  link?: LinkProps;
  image?: MediaType;
}

interface ItemProps {
  color: keyof typeof COLOR_STYLES;
  selected?: boolean;
  image?: MediaType;
}
const Item = ({ color, selected = false, image }: ItemProps) => (
  <p
    className="tw-flex tw-pr-6 tw-items-center tw-cursor-pointer hover:tw-text-action-400"
    aria-current={selected}
  >
    <span
      className={classNames(
        'tw-flex tw-justify-center tw-items-center tw-mr-2 tw-w-7 tw-h-7 tw-rounded-full -tw-rotate-90 tw-bg-[#f6f4f3]',
        selected && 'tw-border-2 tw-border-action-400',
      )}
    >
      {image ? (
        <img
          alt=""
          className="tw-w-5 tw-h-5 tw-rounded-full tw-object-cover tw-object-fit"
          src={image?.url}
        />
      ) : (
        <span
          className={classNames(
            'tw-w-5 tw-h-5 tw-rounded-full',
            color && COLOR_STYLES[color?.toLowerCase() as keyof typeof COLOR_STYLES],
          )}
        />
      )}
    </span>
    <label className="tw-cursor-pointer hover:tw-text-action-400">{color}</label>
  </p>
);

const ColorItem = ({ color, link, image }: ColorItemProps) => {
  const linkObj = link && buildLinkObj(link);

  if (linkObj) {
    return (
      <a href={linkObj.href}>
        <Item color={color} />
      </a>
    );
  }

  return <Item selected color={color} image={image} />;
};

export interface AlternateColorsProps {
  color: keyof typeof COLOR_STYLES;
  colors: Array<ProductColor>;
  dark?: boolean;
  step?: number | `${number}`;
}

const AlternateColors = ({ color, colors, step, dark = false }: AlternateColorsProps) => {
  const { t } = useTranslation();
  const altColors = [{ color }, ...colors];
  return (
    <div className={classNames(dark && 'tw-text-white')}>
      <p className={classNames('tra-text-web-utl-link', dark && 'tw-text-white')}>
        0{step} -{' '}
        {t('productDetailPageContent.selectColorText', undefined, { default: 'Select A Color' })}:{' '}
        {color}
      </p>
      <p className="sr-only">
        {altColors?.length}{' '}
        {t('productDetailPageContent.colorsAvailableText', undefined, {
          default: 'colors available',
        })}
        ,{' '}
        {t('productDetailPageContent.selectedOptionText', undefined, {
          default: 'Selected option is',
        })}{' '}
        {color}
      </p>
      <div className="tw-pt-2 tw-pb-6 tw-flex tw-align-start">
        {altColors?.map((item) => <ColorItem {...item} key={`grill_color_${item.color}`} />)}
      </div>
    </div>
  );
};

export default AlternateColors;
